import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const AttendanceUpdate = () => {
  const todayISO = new Date().toISOString().split('T')[0];
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState(null); // 'csv' or 'pdf'
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [rmEmployees, setRmEmployees] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 25,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [filters, setFilters] = useState({
    employeeId: 'all',
    timePeriod: 'today',
    startDate: todayISO,
    endDate: todayISO
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({
    date: '',
    checkInHour: '09',
    checkInMinute: '00',
    checkInAmPm: 'AM',
    checkOutHour: '06',
    checkOutMinute: '00',
    checkOutAmPm: 'PM',
    locationId: '',
    locationName: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [workLocations, setWorkLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState('');
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('');

  // Check if user is admin or has admin-designated role from env
  const isAdminLike = () => {
    const role = localStorage.getItem('role') || '';
    const designationName = localStorage.getItem('designationName') || '';
    const normalizedDesignation = designationName.replace(/\u00A0/g, ' ').trim();
    const adminDesignation = process.env.REACT_APP_ADMIN_DESIGNATION || '';
    return role === 'admin' || (adminDesignation && normalizedDesignation === adminDesignation.trim());
  };

  // Set employeeId for non-admin users on mount
  useEffect(() => {
    const currentUserId = localStorage.getItem('id') || '';
    
    // If user is not admin-like, set their own employee ID
    if (!isAdminLike() && currentUserId) {
      setFilters(prev => ({
        ...prev,
        employeeId: currentUserId
      }));
    }
  }, []);

  // Fetch employees on mount
  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fetch data when filters or pagination changes
  useEffect(() => {
    // Only fetch if we have valid filters
    if (filters.timePeriod === 'custom') {
      if (filters.startDate && filters.endDate) {
        fetchReportData();
      }
    } else {
      // For non-custom time periods, fetch immediately
      fetchReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.employeeId, filters.timePeriod, filters.startDate, filters.endDate, pagination.currentPage, pagination.limit]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    if (!showExportDropdown) return;

    const handleClickOutside = (event) => {
      const target = event.target;
      const btnGroup = target.closest('.btn-group');
      const exportDropdown = target.closest('.export-dropdown');
      const exportButton = target.closest('button.employee-back-btn');

      if (btnGroup || exportDropdown || exportButton) {
        return;
      }

      setShowExportDropdown(false);
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showExportDropdown]);

  // Fetch work locations when modal opens
  useEffect(() => {
    if (showEditModal) {
      fetchWorkLocations();
    }
  }, [showEditModal]);

  // Update locationId when workLocations are loaded and we have a locationName
  useEffect(() => {
    if (showEditModal && workLocations.length > 0 && editFormData.locationName && !editFormData.locationId) {
      // Try to find matching location
      const foundLocation = workLocations.find(loc => 
        loc.locationName === editFormData.locationName || 
        loc.locationName.toLowerCase() === editFormData.locationName.toLowerCase()
      );
      if (foundLocation) {
        setEditFormData(prev => ({
          ...prev,
          locationId: foundLocation.id,
          locationName: foundLocation.locationName
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workLocations, showEditModal]);

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Convert DD-MM-YYYY to YYYY-MM-DD for date input
    if (dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    return dateString;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      // Convert YYYY-MM-DD to DD-MM-YYYY for display
      if (dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3 && parts[0].length === 4) {
          // Validate date parts
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          const day = parseInt(parts[2], 10);
          if (!isNaN(year) && !isNaN(month) && !isNaN(day) && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
            return `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
          }
        }
      }
      return dateString;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));

    // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, currentPage: 1 }));

    // Auto-set date range based on time period
    if (field === 'timePeriod') {
      const today = new Date();
      let startDate = '';
      let endDate = '';

      switch (value) {
        case 'today':
          startDate = endDate = today.toISOString().split('T')[0];
          break;
        case 'thisWeek':
          // Monday to Sunday of current week
          const weekStart = new Date(today);
          const day = weekStart.getDay();
          const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
          weekStart.setDate(diff);
          startDate = weekStart.toISOString().split('T')[0];
          endDate = today.toISOString().split('T')[0];
          break;
        case 'thisMonth':
          // First to last day of current month
          startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
          endDate = today.toISOString().split('T')[0];
          break;
        case 'lastMonth':
          const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
          startDate = lastMonth.toISOString().split('T')[0];
          endDate = lastMonthEnd.toISOString().split('T')[0];
          break;
        case 'thisYear':
          // January 1 to December 31 of current year
          startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
          endDate = today.toISOString().split('T')[0];
          break;
        default:
          // Custom - keep existing dates
          break;
      }

      if (startDate && endDate) {
        setFilters(prev => ({
          ...prev,
          startDate: formatDateForInput(startDate),
          endDate: formatDateForInput(endDate)
        }));
      }
    }
  };

  const mapTimePeriodToAPI = (timePeriod) => {
    const mapping = {
      'today': 'custom',
      'thisWeek': 'this-week',
      'thisMonth': 'this-month',
      'lastMonth': 'custom',
      'thisYear': 'this-year',
      'custom': 'custom'
    };
    return mapping[timePeriod] || 'this-month';
  };

  const fetchReportData = async () => {
    // Validate date range for custom time period
    if (filters.timePeriod === 'custom') {
      if (!filters.startDate || !filters.endDate) {
        // Don't fetch if dates are not set for custom period
        return;
      }
      // Validate that start date is before end date
      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      if (start > end) {
        toast.error('Start date must be before or equal to end date');
        return;
      }
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const currentUserId = localStorage.getItem('id');
      
      if (!currentUserId) {
        toast.error('User ID not found. Please login again.');
        return;
      }

      const apiTimePeriod = mapTimePeriodToAPI(filters.timePeriod);
      
      // Build payload
      const payload = {
        action: 'get-checkin-checkout-summary',
        currentUserId: currentUserId,
        page: pagination.currentPage,
        limit: pagination.limit,
        timePeriod: apiTimePeriod
      };

    if (filters.employeeId !== 'all') {
      payload.employeeId = filters.employeeId;
    }
      
      // Only include employeeId if user is admin-like and a specific employee is selected
      if (isAdminLike() && filters.employeeId !== 'all') {
        payload.employeeId = filters.employeeId;
      }
      
      // Add date range for custom time period
      if (apiTimePeriod === 'custom') {
        const startDate = formatDateForDisplay(filters.startDate);
        const endDate = formatDateForDisplay(filters.endDate);
        if (startDate && endDate) {
          payload.startDate = startDate;
          payload.endDate = endDate;
        }
      }

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setReportData(result.data || []);
        setPagination(prev => ({
          ...prev,
          currentPage: result.currentPage || prev.currentPage,
          totalPages: result.totalPages || 1,
          total: result.total || 0,
          hasNextPage: (result.currentPage || prev.currentPage) < (result.totalPages || 1),
          hasPreviousPage: (result.currentPage || prev.currentPage) > 1
        }));
        if (result.data && result.data.length === 0) {
          toast.info('No records found for the selected filters.');
        }
      } else {
        toast.error(result?.message || 'Failed to fetch report data');
        setReportData([]);
        setPagination(prev => ({
          ...prev,
          totalPages: 0,
          total: 0,
          hasNextPage: false,
          hasPreviousPage: false
        }));
      }
    } catch (error) {
      // Safely extract error message without circular reference issues
      let errorMessage = 'Something went wrong. Please try again.';
      try {
        if (error?.response?.data?.message) {
          errorMessage = String(error.response.data.message);
        } else if (error?.message) {
          errorMessage = String(error.message);
        }
        // Only log error message, not the entire error object to avoid circular structure issues
        console.error('Error fetching report data:', errorMessage);
      } catch (e) {
        // If there's still an issue, use default message
        errorMessage = 'Failed to fetch report data. Please try again.';
        console.error('Error parsing error message');
      }
      toast.error(errorMessage);
      setReportData([]);
      setPagination(prev => ({
        ...prev,
        totalPages: 0,
        total: 0,
        hasNextPage: false,
        hasPreviousPage: false
      }));
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  const fetchRmEmployees = async () => {
    const currentUserId = localStorage.getItem('id');
    if (!currentUserId || isAdminLike()) return;

    try {
      const res = await axios.post(API_URL, {
        action: 'reporting-employee-filter', 
        currentUserId: currentUserId,        
      });

      if (res.data.success) {
        setRmEmployees(res.data.data || []);
      } else {
        console.error('Failed to fetch RM employees:', res.data.message);
      }
    } catch (err) {
      console.error('Error fetching RM employees:', err);
    }
  };

  fetchRmEmployees();
}, []);

  const fetchEmployees = async () => {
    if (!API_URL) return;

    try {
      const currentUserId = localStorage.getItem('id');
      
      if (!currentUserId) {
        return;
      }

      const payload = {
        action: 'get-employees',
        page: 1,
        limit: 1000
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        const employeeList = result.employees || [];
        // Only show employee dropdown for admin-like users (admin or Managing Director)
        if (isAdminLike()) {
          setEmployees(employeeList);
        } else {
          // For non-admin users, don't show employee dropdown
          setEmployees([]);
        }
      }
    } catch (error) {
      // Silently fail - employees dropdown is optional
      console.error('Error fetching employees:', error);
    }
  };

  const fetchWorkLocations = async () => {
    if (!API_URL) return;

    const userId = localStorage.getItem('id');
    if (!userId) return;

    try {
      const payload = {
        action: 'get-work-locations',
        status: 'active',
        page: 1,
        limit: 100,
        currentUserId: userId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        const locations = result.data || [];
        setWorkLocations(locations);
      }
    } catch (error) {
      console.error('Error fetching work locations:', error);
      // Don't show error toast as it's not critical
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({ ...prev, limit: newLimit, currentPage: 1 }));
  };

  const getPaginationItems = () => {
    const totalPages = pagination.totalPages;
    const currentPage = pagination.currentPage;

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const items = [];
    const addPage = (page) => {
      if (!items.includes(page)) {
        items.push(page);
      }
    };
    const addEllipsis = () => {
      if (items[items.length - 1] !== 'ellipsis') {
        items.push('ellipsis');
      }
    };

    if (currentPage <= 3) {
      for (let page = 1; page <= Math.min(5, totalPages); page += 1) {
        addPage(page);
      }
      if (totalPages > 8) {
        addEllipsis();
      }
      for (let page = Math.max(totalPages - 2, 1); page <= totalPages; page += 1) {
        addPage(page);
      }
    } else if (currentPage >= totalPages - 2) {
      for (let page = 1; page <= Math.min(3, totalPages); page += 1) {
        addPage(page);
      }
      if (totalPages > 8) {
        addEllipsis();
      }
      for (let page = Math.max(totalPages - 4, 1); page <= totalPages; page += 1) {
        addPage(page);
      }
    } else {
      for (let page = Math.max(currentPage - 2, 1); page <= Math.min(currentPage + 2, totalPages); page += 1) {
        addPage(page);
      }
      addEllipsis();
      for (let page = Math.max(totalPages - 3, 1); page <= totalPages; page += 1) {
        addPage(page);
      }
    }

    return items;
  };

  const getSerialNumber = (index) => {
    return (pagination.currentPage - 1) * pagination.limit + index + 1;
  };

  const formatTimeOnly = (timeValue) => {
    if (!timeValue || timeValue === '-') return '-';
    const raw = String(timeValue).trim();

    // Handle cases where date and time are combined like "21-01-2026 10:01 am"
    if (raw.includes(' ')) {
      const parts = raw.split(/\s+/).filter(Boolean);
      const last = parts[parts.length - 1];
      const secondLast = parts[parts.length - 2];
      if (secondLast && last && /(am|pm)/i.test(last)) {
        return `${secondLast} ${last}`.toLowerCase();
      }
      if (last && last.includes(':')) {
        const parsed = convert24To12Hour(last);
        return parsed ? `${parsed.hour}:${parsed.minute} ${parsed.ampm}` : last;
      }
    }

    if (raw.includes(':')) {
      const parsed = convert24To12Hour(raw);
      return parsed ? `${parsed.hour}:${parsed.minute} ${parsed.ampm}` : raw;
    }

    return raw;
  };

  const handleExport = async (format) => {
    if (filters.timePeriod === 'custom' && (!filters.startDate || !filters.endDate)) {
      toast.error('Please select start date and end date before exporting');
      return;
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setExporting(true);
    setExportFormat(format);
    setShowExportDropdown(false);

    try {
      const currentUserId = localStorage.getItem('id');

      if (!currentUserId) {
        toast.error('User ID not found. Please login again.');
        return;
      }

      const apiTimePeriod = mapTimePeriodToAPI(filters.timePeriod);

      const payload = {
        action: 'export-checkin-checkout-summary',
        currentUserId: currentUserId,
        format: format,
        timePeriod: apiTimePeriod
      };

      if (isAdminLike() && filters.employeeId !== 'all') {
        payload.employeeId = filters.employeeId;
      }

      if (apiTimePeriod === 'custom') {
        const startDate = formatDateForDisplay(filters.startDate);
        const endDate = formatDateForDisplay(filters.endDate);
        if (startDate && endDate) {
          payload.startDate = startDate;
          payload.endDate = endDate;
        }
      }

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      });

      let blob = response.data;

      if (!(blob instanceof Blob)) {
        if (typeof response.data === 'string') {
          try {
            const errorData = JSON.parse(response.data);
            toast.error(errorData.message || errorData.error || 'Failed to export');
            return;
          } catch (e) {
            toast.error('Received invalid response from server');
            return;
          }
        }

        blob = new Blob([response.data], { 
          type: format === 'pdf' ? 'application/pdf' : 'text/csv' 
        });
      }

      if (format === 'pdf') {
        const firstChunk = blob.slice(0, 4);
        const arrayBuffer = await firstChunk.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        const header = String.fromCharCode(...bytes);

        if (header !== '%PDF' && /^[A-Za-z0-9+/]/.test(header)) {
          const base64String = await blob.text();

          if (!/^[A-Za-z0-9+/=]+$/.test(base64String.trim())) {
            toast.error('Received invalid data from server');
            return;
          }

          try {
            const binaryString = atob(base64String.trim());
            const binaryBytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              binaryBytes[i] = binaryString.charCodeAt(i);
            }

            blob = new Blob([binaryBytes], { type: 'application/pdf' });

            const decodedHeader = String.fromCharCode(...new Uint8Array(await blob.slice(0, 4).arrayBuffer()));
            if (decodedHeader !== '%PDF') {
              toast.error('Failed to decode PDF. Please check API Gateway configuration.');
              return;
            }
          } catch (decodeError) {
            toast.error('Failed to decode PDF data');
            return;
          }
        }
      }

      if (!blob || blob.size === 0) {
        toast.error('Received an empty file from the server');
        return;
      }

      if (blob.type === 'application/json' || blob.size < 100) {
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          toast.error(errorData.message || errorData.error || 'Failed to export');
          return;
        } catch (e) {
          if (blob.size < 100) {
            toast.error('Received an empty or corrupted file');
            return;
          }
        }
      }

      if (format === 'pdf') {
        if (!blob.type || blob.type === 'application/octet-stream') {
          blob = new Blob([blob], { type: 'application/pdf' });
        }

        const firstChunk = blob.slice(0, 4);
        const arrayBuffer = await firstChunk.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        const header = String.fromCharCode(...bytes);

        if (header !== '%PDF') {
          toast.error('Invalid PDF file received');
          return;
        }
      }

      if (format === 'csv') {
        if (!blob.type || blob.type === 'application/octet-stream') {
          blob = new Blob([blob], { type: 'text/csv' });
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `attendance-report-${timestamp}.${format}`;

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success(`${format.toUpperCase()} file downloaded successfully`);
    } catch (error) {
      console.error('Error exporting attendance report:', error);

      let errorMessage = 'Failed to export report. Please try again.';

      if (error.response) {
        if (error.response.data instanceof Blob) {
          try {
            const errorText = await error.response.data.text();
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch (e) {
            // Not JSON, use default message
          }
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setExporting(false);
      setExportFormat(null);
    }
  };

  const handleViewNotes = (notes, employeeName) => {
    setSelectedNotes(notes || '-');
    setSelectedEmployeeName(employeeName || 'Employee');
    setShowNotesModal(true);
  };

  const closeNotesModal = () => {
    setShowNotesModal(false);
    setSelectedNotes('');
    setSelectedEmployeeName('');
  };

  // Convert 24-hour format (HH:MM:SS or HH:MM) to 12-hour format components
  const convert24To12Hour = (time24) => {
    if (!time24 || time24 === '-' || String(time24).trim() === '') {
      return null;
    }
    try {
      // Handle different time formats: "09:00:00", "09:00", "9:00", etc.
      const timeStr = String(time24).trim();
      const timeParts = timeStr.split(':');
      
      if (timeParts.length < 2) {
        return null;
      }
      
      const hours = timeParts[0];
      const minutes = timeParts[1] || '00';
      const hour24 = parseInt(hours, 10);
      const minute = String(minutes).padStart(2, '0');
      
      if (isNaN(hour24) || hour24 < 0 || hour24 > 23) {
        return null;
      }
      
      let hour12 = hour24 % 12;
      if (hour12 === 0) hour12 = 12;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      
      return {
        hour: String(hour12).padStart(2, '0'),
        minute: minute,
        ampm: ampm
      };
    } catch (error) {
      console.error('Error converting time:', error, time24);
      return null;
    }
  };

  // Convert 12-hour format components to 24-hour format string
  const convert12To24Hour = (hour, minute, ampm) => {
    try {
      let hour24 = parseInt(hour, 10);
      const min = minute || '00';
      
      if (ampm === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (ampm === 'AM' && hour24 === 12) {
        hour24 = 0;
      }
      
      return `${String(hour24).padStart(2, '0')}:${min.padStart(2, '0')}`;
    } catch (error) {
      return '00:00';
    }
  };

  // Calculate total hours between check-in and check-out
  const calculateTotalHours = (checkIn24, checkOut24) => {
    if (!checkIn24 || !checkOut24 || checkIn24 === '-' || checkOut24 === '-') {
      return '-';
    }
    try {
      const [inHours, inMinutes] = checkIn24.split(':').map(Number);
      const [outHours, outMinutes] = checkOut24.split(':').map(Number);
      
      const inTotalMinutes = inHours * 60 + inMinutes;
      const outTotalMinutes = outHours * 60 + outMinutes;
      
      let diffMinutes = outTotalMinutes - inTotalMinutes;
      if (diffMinutes < 0) {
        diffMinutes += 24 * 60; // Handle next day
      }
      
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      
      return `${hours}:${String(minutes).padStart(2, '0')}`;
    } catch (error) {
      return '-';
    }
  };

  // Convert date from DD-MM-YYYY to YYYY-MM-DD for date input
  const convertDateForInput = (dateStr) => {
    if (!dateStr || dateStr === '-') return '';
    try {
      // Date is in DD-MM-YYYY format
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
      return dateStr;
    } catch (error) {
      return '';
    }
  };

  // Convert date from YYYY-MM-DD to DD-MM-YYYY for API
  const convertDateForAPI = (dateStr) => {
    if (!dateStr) return '';
    try {
      // Date is in YYYY-MM-DD format from input
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${day}-${month}-${year}`;
      }
      return dateStr;
    } catch (error) {
      return dateStr;
    }
  };

  // Handle row click to open edit modal
  const handleRowClick = (record) => {
    // Extract employeeId - could be from record.employeeId or record.userId
    const employeeId = record.employeeId || record.userId || localStorage.getItem('id');
    
    setSelectedRecord({
      ...record,
      employeeId: employeeId
    });
    
    // Convert date from DD-MM-YYYY to YYYY-MM-DD for date input
    const dateForInput = convertDateForInput(record.date || '');
    
    // Convert 24-hour times to 12-hour format components
    const checkIn12 = convert24To12Hour(record.checkInTime || '') || { hour: '09', minute: '00', ampm: 'AM' };
    const checkOut12 = convert24To12Hour(record.checkOutTime || '') || { hour: '06', minute: '00', ampm: 'PM' };
    
    // Determine locationId and locationName from record.location
    // Note: workLocations will be loaded when modal opens via useEffect
    let locationId = '';
    let locationName = record.location || '';
    
    if (locationName) {
      // Check if it's "Work From Home"
      if (locationName.toLowerCase().includes('work from home') || locationName.toLowerCase() === 'work from home') {
        locationName = 'Work From Home';
        locationId = 'work-from-home';
      } else {
        // Try to find matching location in workLocations (may be empty initially)
        const foundLocation = workLocations.find(loc => 
          loc.locationName === locationName || 
          loc.locationName.toLowerCase() === locationName.toLowerCase()
        );
        if (foundLocation) {
          locationId = foundLocation.id;
          locationName = foundLocation.locationName;
        }
        // If not found, keep the original locationName and locationId will remain empty
        // The dropdown will show "Select Location" and user can choose
      }
    }
    
    setEditFormData({
      date: dateForInput,
      checkInHour: checkIn12.hour,
      checkInMinute: checkIn12.minute,
      checkInAmPm: checkIn12.ampm,
      checkOutHour: checkOut12.hour,
      checkOutMinute: checkOut12.minute,
      checkOutAmPm: checkOut12.ampm,
      locationId: locationId,
      locationName: locationName
    });
    
    setShowEditModal(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedRecord(null);
    setEditFormData({
      date: '',
      checkInHour: '09',
      checkInMinute: '00',
      checkInAmPm: 'AM',
      checkOutHour: '06',
      checkOutMinute: '00',
      checkOutAmPm: 'PM',
      locationId: '',
      locationName: ''
    });
  };

  // Handle edit attendance
  const handleEditAttendance = async () => {
    if (!selectedRecord) return;

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    const currentUserId = localStorage.getItem('id');
    
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      return;
    }

    // Validate that at least one time is provided
    if (!editFormData.checkInHour && !editFormData.checkOutHour) {
      toast.error('Please provide at least check-in or check-out time');
      return;
    }

    // Convert 12-hour format to 24-hour format
    const checkIn24 = editFormData.checkInHour 
      ? convert12To24Hour(
          editFormData.checkInHour,
          editFormData.checkInMinute,
          editFormData.checkInAmPm
        )
      : null;
    const checkOut24 = editFormData.checkOutHour
      ? convert12To24Hour(
          editFormData.checkOutHour,
          editFormData.checkOutMinute,
          editFormData.checkOutAmPm
        )
      : null;

    // Validate date
    if (!editFormData.date) {
      toast.error('Please select a date');
      return;
    }

    setEditLoading(true);
    try {
      // Convert date from YYYY-MM-DD to DD-MM-YYYY format for API
      const date = convertDateForAPI(editFormData.date);
      
      // Build payload - API expects times in 24-hour format with space at the end
      const payload = {
        action: 'edit-attendance',
        currentUserId: currentUserId,
        employeeId: selectedRecord.employeeId,
        date: date
      };
      
      if (checkIn24) {
        payload.checkInTime = `${checkIn24} `;
      }
      if (checkOut24) {
        payload.checkOutTime = `${checkOut24} `;
      }
      if (editFormData.locationName && editFormData.locationName.trim()) {
        payload.location = editFormData.locationName.trim();
      }

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || 'Attendance updated successfully');
        closeEditModal();
        // Refresh the data
        fetchReportData();
      } else {
        toast.error(result?.message || 'Failed to update attendance');
      }
    } catch (error) {
      let errorMessage = 'Something went wrong. Please try again.';
      try {
        if (error?.response?.data?.message) {
          errorMessage = String(error.response.data.message);
        } else if (error?.message) {
          errorMessage = String(error.message);
        }
        console.error('Error updating attendance:', errorMessage);
      } catch (e) {
        errorMessage = 'Failed to update attendance. Please try again.';
        console.error('Error parsing error message');
      }
      toast.error(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title and Export Button */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-8">
              <h1 className="employee-title">
                Attendance Report
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                View your daily attendance records including check-ins, check-outs, leaves, and holidays.
              </p>
            </div>
            <div className="col-md-4 employee-view-header-actions">
              <div className="btn-group" style={{ position: 'relative', zIndex: 1001 }}>
                <button
                  type="button"
                  className="employee-back-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!exporting) {
                      setShowExportDropdown(prev => !prev);
                    }
                  }}
                  disabled={exporting}
                  style={{ position: 'relative', zIndex: 1001 }}
                >
                  {exporting ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2"></span>
                      Exporting {exportFormat ? `(${exportFormat.toUpperCase()})` : ''}...
                    </>
                  ) : (
                    <>
                      Export
                      <i className={`fas fa-chevron-${showExportDropdown ? 'up' : 'down'} ml-2`}></i>
                    </>
                  )}
                </button>
                {showExportDropdown && !exporting && (
                  <div 
                    className="export-dropdown"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '5px',
                      backgroundColor: '#fff',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      zIndex: 1002,
                      minWidth: '150px',
                      display: 'block',
                      visibility: 'visible'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (reportData.length > 0) {
                          handleExport('csv');
                        } else {
                          toast.warning('No data available to export. Please apply filters first.');
                        }
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 16px',
                        textAlign: 'left',
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: reportData.length === 0 ? '#9ca3af' : '#374151',
                        fontSize: '14px',
                        cursor: reportData.length === 0 ? 'not-allowed' : 'pointer',
                        opacity: reportData.length === 0 ? 0.5 : 1,
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (reportData.length > 0) {
                          e.target.style.backgroundColor = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <i className="fas fa-file-csv mr-2" style={{ color: '#10b981' }}></i>
                      Export as CSV
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (reportData.length > 0) {
                          handleExport('pdf');
                        } else {
                          toast.warning('No data available to export. Please apply filters first.');
                        }
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 16px',
                        textAlign: 'left',
                        border: 'none',
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: 'transparent',
                        color: reportData.length === 0 ? '#9ca3af' : '#374151',
                        fontSize: '14px',
                        cursor: reportData.length === 0 ? 'not-allowed' : 'pointer',
                        opacity: reportData.length === 0 ? 0.5 : 1,
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (reportData.length > 0) {
                          e.target.style.backgroundColor = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <i className="fas fa-file-pdf mr-2" style={{ color: '#ef4444' }}></i>
                      Export as PDF
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card employee-content-card">
                <div className="card-body">
                  <div className="row" style={{ gap: '20px 0' }}>
                    {/* Select Employee - Only show for admin-like users (admin or Managing Director) */}
                    {isAdminLike() && (
                      <div className="col-md-3">
                        <label htmlFor="employee" className="form-label" style={{ marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                          <i className="fas fa-info-circle mr-1" style={{ fontSize: '11px', color: '#6b7280' }}></i>
                          Select Employee
                        </label>
                        <select
                          id="employee"
                          className="form-control"
                          value={filters.employeeId}
                          onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                          style={{ padding: '8px 12px', fontSize: '14px', height: '40px' }}
                        >
                          <option value="all">All Employees</option>
                          {employees.map((emp) => {
                            const empId = emp._id || emp.id;
                            // Prioritize name field (from API response)
                            let empName = emp.name;
                            
                            // Fallback to FirstName + LastName if name doesn't exist
                            if (!empName && emp.FirstName && emp.LastName) {
                              empName = `${emp.FirstName} ${emp.LastName}`.trim();
                            }
                            
                            // Fallback to firstName + lastName if still no name
                            if (!empName && emp.firstName && emp.lastName) {
                              empName = `${emp.firstName} ${emp.lastName}`.trim();
                            }
                            
                            // Final fallback to email or 'Unknown'
                            if (!empName) {
                              empName = emp.email || 'Unknown';
                            }
                            
                            return (
                              <option key={empId} value={empId}>
                                {empName}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    )}

                     {!isAdminLike() && (
                      <div className="col-md-3">
                        <label
                          htmlFor="employee"
                          className="form-label"
                          style={{
                            marginBottom: "5px",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#374151",
                          }}
                        >
                          <i
                            className="fas fa-info-circle mr-1"
                            style={{ fontSize: "11px", color: "#6b7280" }}
                          ></i>
                          Select Employee
                        </label>
                        <select
                          id="rmEmployee"
                          className="form-control"
                          value={filters.employeeId}
                          onChange={(e) =>
                            handleFilterChange("employeeId", e.target.value)
                          }
                          style={{
                            padding: "8px 12px",
                            fontSize: "14px",
                            height: "40px",
                          }}
                        >
                          <option value="all">All Employees</option>
                          {rmEmployees.map((emp) => (
                            <option key={emp.employeeId} value={emp.employeeId}>
                              {emp.employeeName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}


                    {/* Time Period */}
                    <div className={isAdminLike() ? 'col-md-3' : 'col-md-4'}>
                      <label htmlFor="timePeriod" className="form-label" style={{ marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                        Time Period
                      </label>
                      <select
                        id="timePeriod"
                        className="form-control"
                        value={filters.timePeriod}
                        onChange={(e) => handleFilterChange('timePeriod', e.target.value)}
                        style={{ padding: '8px 12px', fontSize: '14px', height: '40px' }}
                      >
                        <option value="custom">Custom</option>
                        <option value="today">Today</option>
                        <option value="thisWeek">This Week</option>
                        <option value="thisMonth">This Month</option>
                        <option value="lastMonth">Last Month</option>
                        <option value="thisYear">This Year</option>
                      </select>
                    </div>

                    {/* Date Range - Only show when Custom is selected */}
                    {filters.timePeriod === 'custom' && (
                      <div className={isAdminLike() ? 'col-md-4' : 'col-md-5'}>
                        <label htmlFor="dateRange" className="form-label" style={{ marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                          <i className="fas fa-info-circle mr-1" style={{ fontSize: '11px', color: '#6b7280' }}></i>
                          Date Range
                        </label>
                        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                          <input
                            type="date"
                            id="startDate"
                            className="form-control"
                            value={filters.startDate}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Validate year is 4 digits
                              if (value) {
                                const year = value.split('-')[0];
                                if (year && year.length === 4) {
                                  handleFilterChange('startDate', value);
                                } else if (year && year.length > 4) {
                                  // If year is more than 4 digits, truncate to 4
                                  const parts = value.split('-');
                                  if (parts[0].length > 4) {
                                    parts[0] = parts[0].substring(0, 4);
                                    handleFilterChange('startDate', parts.join('-'));
                                  }
                                }
                              } else {
                                handleFilterChange('startDate', value);
                              }
                            }}
                            style={{ padding: '8px 12px', fontSize: '14px', height: '40px', flex: 1 }}
                            max="9999-12-31"
                          />
                          <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>to</span>
                          <input
                            type="date"
                            id="endDate"
                            className="form-control"
                            value={filters.endDate}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Validate year is 4 digits
                              if (value) {
                                const year = value.split('-')[0];
                                if (year && year.length === 4) {
                                  handleFilterChange('endDate', value);
                                } else if (year && year.length > 4) {
                                  // If year is more than 4 digits, truncate to 4
                                  const parts = value.split('-');
                                  if (parts[0].length > 4) {
                                    parts[0] = parts[0].substring(0, 4);
                                    handleFilterChange('endDate', parts.join('-'));
                                  }
                                }
                              } else {
                                handleFilterChange('endDate', value);
                              }
                            }}
                            style={{ padding: '8px 12px', fontSize: '14px', height: '40px', flex: 1 }}
                            max="9999-12-31"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Tab Section */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card employee-content-card">
                <div className="card-body">
                  {/* Tab Navigation */}
                  <div className="mb-3" style={{ borderBottom: '2px solid #0094a9' }}>
                    <button
                      type="button"
                      className="btn"
                      style={{
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '600',
                        backgroundColor: '#0094a9',
                        color: '#fff',
                        border: 'none',
                        borderBottom: '2px solid #0094a9',
                        borderRadius: '0',
                        marginBottom: '-2px'
                      }}
                    >
                      Summary
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="table-container">
                    {loading ? (
                      <div className="text-center" style={{ padding: '40px' }}>
                        <Loading size={60} />
                      </div>
                    ) : reportData.length === 0 ? (
                      <div className="text-center" style={{ padding: '40px', color: '#6b7280' }}>
                        <i className="fas fa-filter" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.5 }}></i>
                        <p style={{ fontSize: '16px', margin: 0 }}>Please apply filters to view data</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="employee-table">
                          <thead>
                            <tr>
                              <th>S.No</th>
                              <th style={{ maxWidth: '100px', whiteSpace: 'nowrap' }}>Employee</th>
                              <th style={{ maxWidth: '100px', whiteSpace: 'nowrap' }}>Date</th>
                              <th>Check-in Time</th>
                              <th>Check-in Type</th>
                              <th>Notes</th>
                              <th>Check-out Time</th>
                              <th>Checked Out Type</th>
                              <th>Hours</th>
                              <th>Day</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.map((record, index) => {
                              // For check-in type: use inLocation if available, otherwise use checkInType
                              const checkInTypeDisplay = (record.inLocation && record.inLocation.trim() !== '') 
                                ? record.inLocation 
                                : (record.checkInType || '-');
                              
                              // For check-out type: use outLocation if available, otherwise use checkedOutType
                              const checkOutTypeDisplay = (record.outLocation && record.outLocation.trim() !== '') 
                                ? record.outLocation 
                                : (record.checkedOutType || '-');
                              
                              return (
                                <tr 
                                  key={record.employeeId || record._id || index}
                                  onClick={() => handleRowClick(record)}
                                  style={{ cursor: 'pointer' }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '';
                                  }}
                                >
                                  <td>{getSerialNumber(index)}</td>
                                  <td style={{ maxWidth: '140px' }}>{record.employeeName || '-'}</td>
                                  <td style={{ maxWidth: '100px', whiteSpace: 'nowrap' }}>{record.date || '-'}</td>
                                  <td>
                                    {formatTimeOnly(record.checkInTime)}
                                  </td>
                                  <td>{checkInTypeDisplay}</td>
                                  <td>
                                    {record.notes && record.notes.trim() ? (
                                      <button
                                        type="button"
                                        className="employee-action-btn employee-action-view"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewNotes(record.notes, record.employeeName);
                                        }}
                                        title="View notes"
                                        style={{ 
                                          padding: '2px 6px',
                                          minWidth: 'auto',
                                          fontSize: '12px'
                                        }}
                                      >
                                        <i className="fa-solid fa-eye"></i>
                                      </button>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                  <td>
                                    {formatTimeOnly(record.checkOutTime)}
                                  </td>
                                  <td>{checkOutTypeDisplay}</td>
                                  <td>
                                    {(() => {
                                      if (record.checkInTime && record.checkInTime !== '-' && 
                                          record.checkOutTime && record.checkOutTime !== '-') {
                                        const totalHours = calculateTotalHours(record.checkInTime, record.checkOutTime);
                                        return totalHours !== '-' ? `${totalHours} hrs` : (record.hours || '-');
                                      }
                                      return record.hours ? `${record.hours} hrs` : '-';
                                    })()}
                                  </td>
                                  <td>{record.day || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    
                    {/* Pagination */}
                    {reportData.length > 0 && pagination.totalPages > 0 && (
                      <div className="employee-pagination">
                        <div className="pagination-limit-container" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: 'auto' }}>
                          <label htmlFor="attendancePaginationLimit" style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                            Show:
                          </label>
                          <select
                            id="attendancePaginationLimit"
                            value={pagination.limit}
                            onChange={handleLimitChange}
                            style={{
                              padding: '6px 10px',
                              fontSize: '14px',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              minWidth: '70px'
                            }}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="75">75</option>
                            <option value="100">100</option>
                          </select>
                        </div>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(1)}
                            disabled={pagination.currentPage === 1}
                          >
                            «
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                          >
                            ‹
                          </button>
                          {getPaginationItems().map((item, index) => {
                            if (item === 'ellipsis') {
                              return (
                                <span
                                  key={`ellipsis-${index}`}
                                  className="pagination-btn"
                                  style={{
                                    cursor: 'default',
                                    padding: '6px 10px',
                                    backgroundColor: 'transparent',
                                    borderColor: 'transparent'
                                  }}
                                >
                                  ...
                                </span>
                              );
                            }

                            return (
                              <button
                                key={item}
                                className={`pagination-btn ${pagination.currentPage === item ? 'active' : ''}`}
                                onClick={() => handlePageChange(item)}
                              >
                                {item}
                              </button>
                            );
                          })}
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.totalPages}
                          >
                            ›
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.totalPages)}
                            disabled={pagination.currentPage === pagination.totalPages}
                          >
                            »
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Edit Attendance Modal */}
      {showEditModal && selectedRecord && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Edit Attendance</h3>
              <button className="modal-close" onClick={closeEditModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '15px' }}>
                <p style={{ marginBottom: '5px', fontSize: '14px', color: '#6b7280' }}>
                  <strong>Employee:</strong> {selectedRecord.employeeName || '-'}
                </p>
              </div>
              
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Date
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={editFormData.date}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Validate year is 4 digits
                    if (value) {
                      const year = value.split('-')[0];
                      if (year && year.length === 4) {
                        setEditFormData(prev => ({ ...prev, date: value }));
                      } else if (year && year.length > 4) {
                        // If year is more than 4 digits, truncate to 4
                        const parts = value.split('-');
                        if (parts[0].length > 4) {
                          parts[0] = parts[0].substring(0, 4);
                          setEditFormData(prev => ({ ...prev, date: parts.join('-') }));
                        }
                      }
                    } else {
                      setEditFormData(prev => ({ ...prev, date: value }));
                    }
                  }}
                  style={{ padding: '8px 12px', fontSize: '14px' }}
                  max="9999-12-31"
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Check-in Time
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <select
                    className="form-control"
                    value={editFormData.checkInHour}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, checkInHour: e.target.value }))}
                    style={{ padding: '4px 12px', fontSize: '14px', width: '80px' }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(hour => (
                      <option key={hour} value={String(hour).padStart(2, '0')}>
                        {String(hour).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                  <span style={{ fontSize: '16px', fontWeight: 'bold' }}>:</span>
                  <select
                    className="form-control"
                    value={editFormData.checkInMinute}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, checkInMinute: e.target.value }))}
                    style={{ padding: '4px 12px', fontSize: '14px', width: '80px' }}
                  >
                    {Array.from({ length: 60 }, (_, i) => i).map(minute => (
                      <option key={minute} value={String(minute).padStart(2, '0')}>
                        {String(minute).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                  <select
                    className="form-control"
                    value={editFormData.checkInAmPm}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, checkInAmPm: e.target.value }))}
                    style={{ padding: '4px 12px', fontSize: '14px', width: '80px' }}
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Check-out Time
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <select
                    className="form-control"
                    value={editFormData.checkOutHour}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, checkOutHour: e.target.value }))}
                    style={{ padding: '4px 12px', fontSize: '14px', width: '80px' }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(hour => (
                      <option key={hour} value={String(hour).padStart(2, '0')}>
                        {String(hour).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                  <span style={{ fontSize: '16px', fontWeight: 'bold' }}>:</span>
                  <select
                    className="form-control"
                    value={editFormData.checkOutMinute}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, checkOutMinute: e.target.value }))}
                    style={{ padding: '4px 12px', fontSize: '14px', width: '80px' }}
                  >
                    {Array.from({ length: 60 }, (_, i) => i).map(minute => (
                      <option key={minute} value={String(minute).padStart(2, '0')}>
                        {String(minute).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                  <select
                    className="form-control"
                    value={editFormData.checkOutAmPm}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, checkOutAmPm: e.target.value }))}
                    style={{ padding: '4px 12px', fontSize: '14px', width: '80px' }}
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Location
                </label>
                <select
                  className="form-control"
                  value={editFormData.locationName === 'Work From Home' ? 'work-from-home' : editFormData.locationId}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    if (selectedValue === 'work-from-home') {
                      setEditFormData(prev => ({ 
                        ...prev, 
                        locationId: '', 
                        locationName: 'Work From Home' 
                      }));
                    } else {
                      const selectedLoc = workLocations.find(loc => loc.id === selectedValue);
                      const locationName = selectedLoc ? selectedLoc.locationName : '';
                      
                      setEditFormData(prev => ({ 
                        ...prev, 
                        locationId: selectedValue,
                        locationName: locationName
                      }));
                    }
                  }}
                  style={{ padding: '4px 12px', fontSize: '14px' }}
                >
                  <option value="">Select Location</option>
                  <option value="work-from-home">Work From Home</option>
                  {workLocations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.locationName}
                    </option>
                  ))}
                </select>
                {workLocations.length === 0 && (
                  <small className="text-muted" style={{ fontSize: '12px', color: '#6b7280' }}>
                    Loading locations...
                  </small>
                )}
              </div>

              {/* Display calculated total hours */}
              {editFormData.checkInHour && editFormData.checkOutHour && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f0fdf4', 
                  borderRadius: '6px',
                  border: '1px solid #86efac',
                  marginBottom: '15px'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#166534', fontWeight: '600' }}>
                    <i className="fas fa-clock mr-2"></i>
                    <strong>Total Hours:</strong> {
                      (() => {
                        try {
                          const checkIn24 = convert12To24Hour(
                            editFormData.checkInHour,
                            editFormData.checkInMinute,
                            editFormData.checkInAmPm
                          );
                          const checkOut24 = convert12To24Hour(
                            editFormData.checkOutHour,
                            editFormData.checkOutMinute,
                            editFormData.checkOutAmPm
                          );
                          const total = calculateTotalHours(checkIn24, checkOut24);
                          return total !== '-' ? `${total} hrs` : 'Calculating...';
                        } catch (error) {
                          return 'N/A';
                        }
                      })()
                    }
                  </p>
                </div>
              )}

              {/* <div style={{ 
                padding: '12px', 
                backgroundColor: '#f0f9ff', 
                borderRadius: '6px',
                border: '1px solid #bae6fd',
                marginTop: '15px'
              }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#0369a1' }}>
                  <i className="fas fa-info-circle mr-2"></i>
                  <strong>Note:</strong> At least one time (check-in or check-out) must be provided. Times will be converted to 24-hour format when saved.
                </p>
              </div> */}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeEditModal}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleEditAttendance}
                disabled={editLoading}
                style={{ backgroundColor: '#0094a9', borderColor: '#0094a9' }}
              >
                {editLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                    Updating...
                  </>
                ) : (
                  'Update Attendance'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="modal-overlay" onClick={closeNotesModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Notes</h3>
              <button className="modal-close" onClick={closeNotesModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '10px', fontSize: '14px', color: '#6b7280' }}>
                <strong>Employee:</strong> {selectedEmployeeName}
              </p>
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#f9fafb', 
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                minHeight: '60px',
                maxHeight: '300px',
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#374151'
              }}>
                {selectedNotes}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeNotesModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceUpdate;
